/**
 * Keyboard shortcut cheat sheet: what to show for the current screen.
 * Keep this in sync when you add or change page-level keys.
 */

import { goHelpItemsForSheet, goPrefixEnabled } from "./goShortcuts";

export const EVERYWHERE_SHORTCUTS = [
  { keys: "⌘/", label: "Show or hide this list" },
  { keys: "Esc", label: "Close, step back, or click away" },
];

const DEFAULT_DIALOG = [
  { keys: "⌘Enter", label: "Save" },
  { keys: "Esc", label: "Close" },
];

const ITEM_EDIT_DIALOG = [
  { keys: "⌘Enter", label: "Save" },
  { keys: "Esc", label: "Close" },
  { keys: "PgUp PgDn", label: "Scroll" },
  { keys: "⌘↑ ⌘↓", label: "Jump to top or bottom" },
];

const CALCULATOR_DIALOG = [
  { keys: "⌘Enter", label: "Apply" },
  { keys: "Esc", label: "Close" },
];

const DASHBOARD = [
  { keys: "N", label: "New deal" },
  { keys: "V", label: "Card or list view" },
  { keys: "↑ ↓", label: "Move between deals" },
  { keys: "Enter", label: "Open the focused deal" },
  { keys: "Space", label: "Select the focused deal" },
  { keys: "⌘A", label: "Select all" },
  { keys: "A U R", label: "Active, upcoming, or archive (when selected)" },
  { keys: "Delete", label: "Delete selected or focused deal" },
];

const TRANSACTION_COMMON = [
  { keys: "1 2 3 4 5", label: "Timeline, Deal, Docs, Shares, Tasks" },
  { keys: "V", label: "Toggle Timeline and Deal" },
  { keys: "W", label: "Write contract" },
  { keys: "B", label: "Back" },
];

const TRANSACTION_TIMELINE = [
  ...TRANSACTION_COMMON,
  { keys: "G", label: "List, card, or calendar" },
  { keys: "N", label: "New clock" },
  { keys: "E", label: "Edit the focused clock" },
  { keys: "S", label: "Select the focused clock" },
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between clocks" },
  { keys: "Delete", label: "Delete selected clocks" },
];

const TRANSACTION_DEAL = [
  ...TRANSACTION_COMMON,
  { keys: "E", label: "Edit deal" },
  { keys: "S", label: "Shares" },
  { keys: "C", label: "Contract mode" },
  { keys: "← →", label: "Cycle View as (when share links exist)" },
];

const TRANSACTION_CONTRACT = [
  ...TRANSACTION_COMMON,
  { keys: "P", label: "Create PDF" },
  { keys: "C", label: "Leave contract mode" },
];

const TRANSACTION_DOCS = [
  ...TRANSACTION_COMMON,
  { keys: "N", label: "Upload Document" },
];

const TRANSACTION_SHARE = [
  ...TRANSACTION_COMMON,
  { keys: "N", label: "New share" },
];

const TRANSACTION_TASKS = [
  ...TRANSACTION_COMMON,
  { keys: "N", label: "New task (owner)" },
  { keys: "E", label: "Edit the focused task (owner)" },
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between tasks" },
  { keys: "← →", label: "Move along a task row" },
  { keys: "Delete", label: "Delete the focused task (owner)" },
];

const UNIFIED = [
  { keys: "G", label: "List, grouped, or calendar" },
  { keys: "Shift+G", label: "Expand or collapse all groups" },
  { keys: "N", label: "New clock" },
  { keys: "O", label: "Open the focused deal" },
  { keys: "E", label: "Edit the focused clock" },
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between rows" },
  { keys: "Delete", label: "Delete the focused clock" },
];

const TEMPLATE_EDIT = [
  { keys: "N", label: "New item" },
  { keys: "V", label: "Timeline or detail" },
  { keys: "R", label: "Reorder mode" },
  { keys: "E", label: "Edit selected items" },
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between items" },
  { keys: "Delete", label: "Remove selected items" },
];

const BROKER = [
  { keys: "1 2 3 4 5", label: "Alerts, Pipeline, Commissions, Agents, Files" },
  { keys: "/", label: "Search Pipeline" },
  { keys: "N", label: "Invite (Agents)" },
  { keys: "B", label: "Agent home" },
  { keys: "↑ ↓", label: "Move between rows" },
  { keys: "Enter", label: "Open the focused file or agent" },
];

const TEMPLATES = [
  { keys: "N", label: "New template" },
  { keys: "/", label: "Search" },
  { keys: "B", label: "Agent home" },
  { keys: "↑ ↓", label: "Move between templates" },
  { keys: "Enter", label: "Open the focused template" },
];

const TC_PERFORMANCE = [
  { keys: "1", label: "My deals" },
  { keys: "2", label: "Whole Team" },
  { keys: "B", label: "Agent home" },
  { keys: "↑ ↓", label: "Move between deals" },
  { keys: "Enter", label: "Open the focused deal" },
];

const TC_TRANSACTIONS = [
  { keys: "1", label: "Transactions list" },
  { keys: "2", label: "My Tasks" },
  { keys: "/", label: "Search" },
  { keys: "B", label: "Agent home" },
  { keys: "↑ ↓", label: "Move between deals" },
  { keys: "Enter", label: "Open the focused deal" },
];

const TC_TASKS = [
  { keys: "1", label: "Transactions list" },
  { keys: "2", label: "My Tasks" },
  { keys: "/", label: "Search" },
  { keys: "B", label: "Agent home" },
  { keys: "N", label: "New task (owner)" },
  { keys: "E", label: "Edit the focused task (owner)" },
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between tasks" },
  { keys: "← →", label: "Move along a task row" },
  { keys: "Enter", label: "Expand the focused task" },
  { keys: "Delete", label: "Delete the focused task (owner)" },
];

const SETTINGS_TABS = [
  { keys: "↑ ↓", label: "Move between Settings tabs" },
];

const SETTINGS_LIBRARY = [
  { keys: "⌘Enter", label: "Save card, or add that person to the library" },
  { keys: "Esc", label: "Cancel edit" },
];

const SHARED = [
  { keys: "X", label: "Expand all" },
  { keys: "C", label: "Collapse all" },
  { keys: "↑ ↓", label: "Move between clocks" },
];

const PUBLIC_PREFIXES = [
  "/login",
  "/privacy",
  "/terms",
  "/faq",
  "/tools",
  "/learn",
  "/unsubscribe",
  "/tc-invite",
  "/invite",
];

export function isShortcutHelpEnabled(pathname) {
  if (!pathname) return false;
  if (pathname === "/") return false;
  return !PUBLIC_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

export function screenIdFromPath(pathname) {
  if (!pathname) return "generic";
  if (pathname === "/dashboard") return "dashboard";
  if (pathname.startsWith("/transaction/")) return "transaction";
  if (pathname.startsWith("/templates/") && pathname !== "/templates") {
    return "template-edit";
  }
  if (pathname === "/templates") return "templates";
  if (pathname === "/unified") return "unified";
  if (pathname === "/tc-dashboard") return "tc-dashboard";
  if (pathname === "/tc-performance") return "tc-performance";
  if (pathname === "/broker") return "broker";
  if (pathname === "/settings") return "settings";
  if (pathname === "/admin") return "admin";
  if (pathname.startsWith("/shared/")) return "shared";
  return "generic";
}

export function dealHelpRoom(pageView, contractMode = false) {
  if (contractMode) return "contract";
  if (pageView === "timeline") return "timeline";
  if (pageView === "tasks") return "tasks";
  if (pageView === "documents") return "documents";
  if (pageView === "share") return "share";
  return "deal";
}

/** ⌘/ on Mac, Ctrl+/ on Windows. Works even while the cursor is in a field. */
export function isShortcutHelpToggleKey(e) {
  if (!e || e.repeat || e.isComposing) return false;
  if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return false;
  return e.key === "/" || e.code === "Slash";
}

function thisScreenShortcuts(screenId, room) {
  switch (screenId) {
    case "dashboard":
      return DASHBOARD;
    case "transaction":
      if (room === "timeline") return TRANSACTION_TIMELINE;
      if (room === "tasks") return TRANSACTION_TASKS;
      if (room === "documents") return TRANSACTION_DOCS;
      if (room === "share") return TRANSACTION_SHARE;
      if (room === "contract") return TRANSACTION_CONTRACT;
      return TRANSACTION_DEAL;
    case "unified":
      return UNIFIED;
    case "template-edit":
      return TEMPLATE_EDIT;
    case "tc-dashboard":
      return room === "tasks" ? TC_TASKS : TC_TRANSACTIONS;
    case "broker":
      return BROKER;
    case "templates":
      return TEMPLATES;
    case "tc-performance":
      return TC_PERFORMANCE;
    case "settings":
      return room === "library" ? [...SETTINGS_TABS, ...SETTINGS_LIBRARY] : SETTINGS_TABS;
    case "shared":
      return room === "timeline" ? SHARED : [];
    default:
      return [];
  }
}

export function dialogShortcuts(dialogId) {
  if (!dialogId) return [];
  if (dialogId === "item-edit") return ITEM_EDIT_DIALOG;
  if (dialogId === "calculator") return CALCULATOR_DIALOG;
  return DEFAULT_DIALOG;
}

function isShortcutHelpDialogNode(node) {
  if (!node) return false;
  return (
    node.hasAttribute("data-shortcut-help-dialog") ||
    node.getAttribute("data-testid") === "shortcut-help-dialog"
  );
}

/**
 * Which dialog is under the cheat sheet, if any.
 * Last matching window wins (calculator on top of a clock editor).
 * Real Radix dialogs use role=dialog; they do not always set data-radix-dialog-content.
 */
export function detectOpenShortcutDialog() {
  if (typeof document === "undefined") return null;
  const nodes = document.querySelectorAll(
    '[role="dialog"], [role="alertdialog"], [data-radix-dialog-content], [data-radix-alert-dialog-content]'
  );
  let foundOther = false;
  let specific = null;
  nodes.forEach((node) => {
    if (isShortcutHelpDialogNode(node)) return;
    if (node.getAttribute("data-state") === "closed") return;
    foundOther = true;
    const id = node.getAttribute("data-shortcut-dialog");
    if (id) specific = id;
  });
  if (specific) return specific;
  if (foundOther) return "default";
  return null;
}

export function buildShortcutHelp(pathname, room = null, dialogId = null) {
  const screenId = screenIdFromPath(pathname);
  const thisScreen = thisScreenShortcuts(screenId, room);
  return {
    enabled: isShortcutHelpEnabled(pathname),
    screenId,
    thisDialog: dialogShortcuts(dialogId),
    thisScreen,
    go: goPrefixEnabled(pathname) ? goHelpItemsForSheet() : [],
    everywhere: EVERYWHERE_SHORTCUTS,
  };
}
